import { Alert } from '@warp-ds/react/components/alert';
import { Button } from '@warp-ds/react/components/button';
import { TextField } from '@warp-ds/react/components/textfield';
import { Toggle } from '@warp-ds/react/components/toggle';
import cn from 'classnames';
import { type FormEvent, useEffect, useReducer, useState } from 'react';
import { useConfig } from 'shared/config/ConfigContext';
import { t } from 'shared/locale/texts';
import { storeLastSearchPrepackage } from 'shared/search/common/lastSearch';
import { trackSearchClick } from 'shared/search/common/tracking';
import { FrontpageDatePickerWrapper } from '../../../components/warp/frontpageDatePickerWrapper/FrontpageDatePickerWrapper';

import { getOneYearFromToday } from '../../../util/calendar';
import { buildParams } from '../../prepackage/buildParams';
import actions, { reducer } from '../../prepackage/prePackageActions';
import { ppDefaultState } from '../../prepackage/prePackageStates';
import { Autocomplete } from '../common/autocomplete/Autocomplete';
import { CustomSelect } from '../common/CustomSelect';
import { SearchPanelWrapper } from '../common/SearchPanelWrapper';
import { dateSelectionOptions } from './data/dateSelectionOptions';
import { PrePackageSearchOriginOptions } from './data/PrePackageSearchOriginOptions';
import { FlexibleDateSelection } from './FlexibleDateSelection';
import { useDestinationHandler } from './hooks/useDestinationHandler';
import { usePrePackageAutocompleteMapper } from './hooks/usePrePackageAutocompleteMapper';
import { usePrePackageFacets } from './hooks/usePrePackageFacets';
import { usePrePackageInitialState } from './hooks/usePrePackageInitialState';
import { usePrePackageValidation } from './hooks/usePrePackageValidation';
import { PrePackageHiddenInputs } from './PrePackageHiddenInputs';
import styles from './PrePackageSearch.module.css';
import { buildPrePackageTrackingItem } from './util/buildPrePackageTrackingItem';

type PrePackageSearchProps = {
    prefill?: Partial<typeof ppDefaultState>;
    position: string;
    unleash?: Record<string, boolean> | null; // cannot be just undefined as that is not serializable (toggles are returned from getSSP)
};

export const PrePackageSearch = ({ prefill, position }: PrePackageSearchProps) => {
    const [pristine, setPristine] = useState(true);
    const [state, dispatch] = useReducer(reducer, ppDefaultState);

    const ready = usePrePackageInitialState(dispatch, prefill);

    const { ppAutocompleteUrl, ppFacetUrl, ppResultUrl } = useConfig();

    const facets = state.facetData?.[buildParams(state)];
    const lookupCount = (id: string): number => (facets ? facets[id] || 0 : 0);

    const { validateState, invalid, validationErrors } = usePrePackageValidation(state, pristine);
    usePrePackageFacets({ state, dispatch, facetUrl: ppFacetUrl });
    const autocompleteMapper = usePrePackageAutocompleteMapper(lookupCount);
    const handleOnSelect = useDestinationHandler(dispatch);

    const handleSubmit = (event: FormEvent<HTMLFormElement>): void => {
        setPristine(false);
        if (validateState()) {
            trackSearchClick('Search prepackage', '/reise/pakkereiser', 'prepackage', position, buildPrePackageTrackingItem(state));
            storeLastSearchPrepackage({ ...state, facetData: undefined });
        } else {
            event.preventDefault();
        }
    };

    const [hasNoOffers, setHasNoOffers] = useState(false);

    useEffect(() => {
        const timeout = setTimeout(() => {
            if (facets && state.destinationId) {
                const count = lookupCount(state.destinationId);
                setHasNoOffers(count === 0);
            }
        }, 10);
        return () => clearTimeout(timeout);
    }, [facets, state.destinationId]);

    const anchorHelpText = Array.from(new Set([validationErrors.departureDate, validationErrors.returnDate].filter(Boolean))).join('. ');

    return (
        <SearchPanelWrapper onSubmit={handleSubmit} action={ppResultUrl} method="GET" dataTestId="prepackageSearch">
            <div className={styles['form-header']}>
                <Toggle
                    type="radio"
                    options={dateSelectionOptions}
                    selected={ready && state.searchType ? dateSelectionOptions.filter((opt) => opt.value === state.searchType) : []}
                    onChange={(selected) => dispatch(actions.setSearchType(selected.value))}
                    className="flex flex-row gap-8"
                />
            </div>
            <div className={styles['form-body']}>
                <div className={styles['form-body__row']}>
                    <CustomSelect
                        relative
                        fullWidth
                        fullscreenTitle="Hvor vil du reise fra?"
                        triggerLabel={t('prepackage.origin.label')}
                        options={PrePackageSearchOriginOptions}
                        value={state.originAirport}
                        onChange={(e) => dispatch(actions.setOriginAirport(e))}
                    />
                    <Autocomplete
                        vertical="prepackage"
                        id="destination"
                        label={t('prepackage.destination.label')}
                        placeholder={t('prepackage.destination.placeholder')}
                        defaultValue={state.destination}
                        url={ppAutocompleteUrl}
                        mapOptions={autocompleteMapper}
                        error={validationErrors.destination || undefined}
                        departure={false}
                        showDefaultOptionOnFocus
                        onSelect={handleOnSelect}
                    />
                </div>
                {state.searchType === 'flexible' ? (
                    <div className={styles['form-body__flexible-dates']}>
                        <FlexibleDateSelection travelMonths={state.travelMonths} duration={state.duration} dispatch={dispatch} />
                    </div>
                ) : (
                    <FrontpageDatePickerWrapper
                        upperDateLimit={getOneYearFromToday()}
                        startDate={state.departureDate}
                        endDate={state.returnDate}
                        useRange
                        onDateChange={(newDates) => dispatch(actions.setDates(newDates))}
                        useAnchor={{
                            invalid: invalid.departureDate || invalid.returnDate,
                            helpText: anchorHelpText || undefined,
                        }}>
                        <TextField
                            id="date-range-start"
                            label={t('prepackage.departureLabel')}
                            invalid={invalid.departureDate}
                            helpText={validationErrors.departureDate || undefined}
                        />
                        <TextField
                            id="date-range-end"
                            label={t('prepackage.returnLabel')}
                            invalid={invalid.returnDate}
                            helpText={validationErrors.returnDate || undefined}
                        />
                    </FrontpageDatePickerWrapper>
                )}
                <Button
                    className={cn(styles['search-button'], {
                        [styles['search-button--error']]: invalid && Object.values(invalid).some((v) => v),
                    })}
                    fullWidth
                    type="submit"
                    primary
                    data-testid="prepackageSearchButtonNew">
                    {t('action.search')}
                </Button>
            </div>
            <PrePackageHiddenInputs
                originAirport={state.originAirport}
                destinationId={state.destinationId}
                departureDate={state.departureDate}
                returnDate={state.returnDate}
                flexibleDeparture={state.flexibleDeparture}
                travelMonths={state.travelMonths}
                duration={state.duration}
                searchType={state.searchType}
            />
            <Alert type="info" show={hasNoOffers} data-testid="noOffersWarning" className="mt-8">
                {t('prepackage.validation.noOffers')}
            </Alert>
        </SearchPanelWrapper>
    );
};
